/**
 * The measurement-coverage strip, and the reading key that explains it.
 *
 * ---------------------------------------------------------------------------
 * THE ONE THING MOST MONITORING UIs HIDE
 * ---------------------------------------------------------------------------
 * Per-signal honesty does not aggregate. A page of eleven cards, each correctly
 * showing `unproven`, still LOOKS calm — the composition says "lots of things,
 * all fine" even though every individual chip is telling the truth. So every
 * page states its own coverage, once, in a line: of the N measurements here,
 * how many did we actually make.
 *
 * It is ONE LINE and a 3 px rail, not a card. In the previous round this was a
 * full panel with a title, a legend and a tally of coloured chips, at the top
 * of every page — it competed with the page it was describing. A footnote that
 * shouts is a footnote you learn to skip.
 *
 * The rail's segments are separated by fill weight, not by hue: solid ink for
 * what was measured, a fainter block for what was not. The SENTENCE next to it
 * carries the numbers, because a bar is the worst possible way to convey "3".
 *
 * Everything here is derived from the payload already on screen (`lib/coverage`).
 * No endpoint, no field, no score.
 */

import { useState } from 'react';
import type { ReactNode } from 'react';

import { type Coverage, byEpistemic, coverageSentence } from '../lib/coverage';
import { describeStatus, labelFor } from '../lib/signal';
import { Icon } from './icons';
import { StatusPill } from './primitives';

export function CoverageStrip({
  coverage,
  onOpenKey,
}: {
  coverage: Coverage;
  onOpenKey?: () => void;
}): ReactNode {
  if (coverage.total === 0) return null;
  const buckets = byEpistemic(coverage);
  const unknown = buckets.unmeasured + buckets.unmeasurable;
  const pct = (n: number): string => `${String((n / coverage.total) * 100)}%`;

  return (
    <section
      aria-label="measurement coverage for this page"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 px-1"
    >
      <Icon name="coverage" size={14} className="text-ink-4" />

      {/*
        The rail. Small and fixed-width — it is a sparkline for one ratio, not
        a page-wide underline. Decorative: the sentence beside it is the text
        alternative and carries the same numbers.
      */}
      <span aria-hidden="true" className="flex h-[3px] w-20 gap-px overflow-hidden rounded-full">
        {buckets.measured > 0 && (
          <span className="h-full bg-ink-3" style={{ width: pct(buckets.measured) }} />
        )}
        {buckets.unmeasured > 0 && (
          <span className="h-full bg-ink-4/45" style={{ width: pct(buckets.unmeasured) }} />
        )}
        {buckets.unmeasurable > 0 && (
          <span className="h-full bg-ink-4/20" style={{ width: pct(buckets.unmeasurable) }} />
        )}
      </span>

      <p className="text-xs text-ink-3">
        {unknown === 0 ? (
          <>
            all <span className="num font-medium text-ink-2">{coverage.total}</span> measurements on
            this page were made
          </>
        ) : (
          <>
            <span className="num font-medium text-ink-2">{buckets.measured}</span> of{' '}
            <span className="num font-medium text-ink-2">{coverage.total}</span> measurements made
            {buckets.unmeasured > 0 && (
              <>
                {' · '}
                <span className="num">{buckets.unmeasured}</span> not measured
              </>
            )}
            {buckets.unmeasurable > 0 && (
              <>
                {' · '}
                <span className="num">{buckets.unmeasurable}</span> cannot be
              </>
            )}
          </>
        )}
      </p>

      {onOpenKey !== undefined && (
        <button
          type="button"
          onClick={onOpenKey}
          className="ml-auto flex items-center gap-1.5 rounded-lg px-2 text-2xs text-ink-3 transition-colors hover:text-ink"
        >
          <Icon name="key" size={13} />
          reading key
        </button>
      )}

      <p className="sr-only">{coverageSentence(coverage)}</p>
    </section>
  );
}

/**
 * The reading key.
 *
 * Every state, its container, its icon and one sentence about what it means for
 * the world. This is where the prose lives — ALL of it. The previous round
 * repeated an explanation under every row that carried a non-verdict; the rows
 * now carry two words and a glyph, and the paragraph that justifies them is
 * here, one tap away from every page.
 */
export function ReadingKey({ open, onClose }: { open: boolean; onClose: () => void }): ReactNode {
  if (!open) return null;
  const rows: { status: string; word?: string }[] = [
    { status: 'ok' },
    { status: 'empty' },
    { status: 'unproven' },
    { status: 'blocked' },
    { status: 'error' },
    { status: 'above_top_bucket' },
    { status: 'below_request_floor' },
  ];
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="reading key"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={onClose}
    >
      <div
        className="max-h-[85dvh] w-full max-w-xl overflow-y-auto rounded-t-2xl border border-line bg-surface p-6 sm:rounded-2xl"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Reading key</h2>
            <p className="mt-1.5 text-xs leading-relaxed text-ink-3">
              A <span className="text-ink-2">filled</span> chip means we measured it. A{' '}
              <span className="text-ink-2">dotted</span> one means we did not look, or looking
              failed. A <span className="text-ink-2">dashed</span> one means looking cannot work
              today. Colour appears only when something needs a decision.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="close"
            className="flex shrink-0 items-center justify-center rounded-lg border border-line px-3 text-ink-3 transition-colors hover:border-line-strong hover:text-ink"
          >
            <Icon name="close" size={16} />
          </button>
        </div>

        <ul className="mt-5 space-y-3.5">
          {rows.map((row) => (
            <li key={row.status} className="flex flex-col gap-1.5 sm:flex-row sm:gap-4">
              <span className="w-44 shrink-0 pt-0.5">
                <StatusPill status={row.status}>{row.word ?? labelFor(row.status)}</StatusPill>
              </span>
              <span className="text-xs leading-relaxed text-ink-3">
                {describeStatus(row.status)}
              </span>
            </li>
          ))}
        </ul>

        <div className="mt-5 space-y-2 border-t border-line pt-4 text-2xs leading-relaxed text-ink-3">
          <p>
            Charts speak the same language: a window in which no series had a sample is drawn as a
            dotted field with a dotted baseline, so a hole can never be read as a flat, healthy
            line.
          </p>
          <p>
            The coverage line at the top of each page counts the same states across everything on
            screen, because per-chip honesty does not aggregate on its own.
          </p>
        </div>
      </div>
    </div>
  );
}

/** The open/close state for the reading key, shared by the shell. */
export function useReadingKey(): {
  open: boolean;
  show: () => void;
  hide: () => void;
} {
  const [open, setOpen] = useState(false);
  return {
    open,
    show: () => {
      setOpen(true);
    },
    hide: () => {
      setOpen(false);
    },
  };
}
